import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  Users,
  Shield,
  Zap,
  Target,
  CheckCircle2,
  Download,
  Mail,
  Phone,
  Globe,
  Layers,
  MessageSquare,
  Hospital,
  ShieldCheck,
  MapPin,
  Award,
  DollarSign,
  BarChart3,
  Rocket,
  ExternalLink,
  Store,
  Landmark,
  Home
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Logos
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const STERILEMATE_LOGO = "https://customer-assets.emergentagent.com/job_medrec-app-5/artifacts/vhygpw4i_Sterile%20Mate%20Logo%20%282%29.jpg";

export default function InvestorOnePagerPage() {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = () => {
    setDownloading(true);
    // In production, this would generate/download a PDF
    setTimeout(() => {
      setDownloading(false);
      window.print();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* Header - Hide on print */}
      <div className="bg-slate-900 text-white py-4 print:hidden">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <Link to="/investor" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Investor Portal
          </Link>
          <Button onClick={handleDownloadPDF} disabled={downloading} className="bg-cyan-600 hover:bg-cyan-700">
            <Download className="w-4 h-4 mr-2" />
            {downloading ? "Preparing..." : "Print / Save PDF"}
          </Button>
        </div>
      </div>

      {/* One-Pager Content */}
      <div className="max-w-5xl mx-auto px-6 py-8 print:py-4 print:px-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 pb-6 border-b-2 border-slate-200">
          <div className="flex items-center gap-4">
            <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="w-16 h-16 object-cover rounded-lg" />
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Sterile Mate LLC</h1>
              <p className="text-lg text-emerald-600 font-medium">SBA 7(a) Expansion &mdash; Real Estate + Healthcare Platform</p>
            </div>
          </div>
          <div className="text-right text-sm text-slate-600">
            <p className="font-semibold text-slate-900">SBA 7(a) Loan Request</p>
            <p>$3,415,000 Total Project</p>
            <p>20121 Highway 59 N, Humble, TX</p>
          </div>
        </div>

        {/* Enterprise Validation Banner */}
        <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 rounded-xl p-4 mb-6 text-white">
          <div className="flex items-center gap-3">
            <Hospital className="w-8 h-8" />
            <div>
              <p className="font-bold text-lg">$1.182M Confirmed Pipeline for 2026 &mdash; Harris Health System</p>
              <p className="text-emerald-100 text-sm">$791K Water Softening Systems | $391K Dialysis Water Purification (New Hospital) | Harris County &amp; State of TX Contracts</p>
            </div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Left Column */}
          <div className="space-y-5">
            {/* The Business */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                The Business
              </h2>
              <p className="text-sm text-slate-600">
                Sterile Mate LLC is a healthcare solutions company specializing in <strong>AAMI ST108-compliant water purification systems</strong>, medical device distribution, EVS/janitorial solutions, and the <strong>AskIsaac-automated Backorder Sourcing platform</strong> \u2014 our 6-year benchmark and door-opener into every Texas hospital system. Founded in 2020 during COVID-19 as a frontline triage supplier for Houston hospitals, the company has grown to $1.1M in annual revenue serving hospitals, healthcare systems, and government agencies.
              </p>
            </div>

            {/* The Expansion */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Landmark className="w-5 h-5 text-cyan-500" />
                The Expansion
              </h2>
              <p className="text-sm text-slate-600 mb-3">
                Acquiring a <strong>10,766 SF freestanding commercial building</strong> on 0.92 acres to establish a fully integrated B2B Healthcare Solutions HQ with retail medical supply storefront.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="font-semibold text-blue-800 text-sm flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> Phase I
                  </p>
                  <p className="text-xs text-slate-600 mt-1">B2B HQ + Showroom + &ldquo;The Supply Doctor&rdquo; retail medical supply store (DBA)</p>
                </div>
                <div className="bg-violet-50 rounded-lg p-3 border border-violet-100">
                  <p className="font-semibold text-violet-800 text-sm flex items-center gap-1">
                    <Home className="w-4 h-4" /> Phase II
                  </p>
                  <p className="text-xs text-slate-600 mt-1">DME Depot (Vive Health, MHC Medical) + Medicare/Insurance billing capability</p>
                </div>
              </div>
            </div>

            {/* Revenue Model */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Diversified Revenue Model
              </h2>
              <ul className="space-y-1 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span><strong>B2B Contracts:</strong> Water purification, EVS, medical device infrastructure</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Retail (The Supply Doctor):</strong> Mobility, rehab, home health &mdash; cash-pay + insurance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span><strong>DME/Insurance:</strong> Recurring revenue from Medicare/private insurer reimbursements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Automated Backorder Sourcing (Flagship):</strong> AskIsaac RFQ engine \u2014 Sterile Mate\u2019s 6-year benchmark, now automated and scaling regionally / statewide</span>
                </li>
              </ul>
            </div>

            {/* Key Differentiators */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Why This Deal Is Strong
              </h2>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Immediate revenue:</strong> Retail from Day 1 + $791K B2B pipeline in 90 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Real estate asset:</strong> Appreciating commercial property in high-traffic corridor</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>3 revenue streams:</strong> B2B + Retail + DME/Insurance &mdash; recession-resilient</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span><strong>HUB Certified:</strong> Active government contracts (VA, Harris County, State of TX)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Traction */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Traction &amp; Validation
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">$3.5M+</p>
                  <p className="text-xs text-slate-600">Cumulative Revenue</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">$1.1M</p>
                  <p className="text-xs text-slate-600">2024 Revenue</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">$1.182M</p>
                  <p className="text-xs text-slate-600">2026 Pipeline</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-900">HUB</p>
                  <p className="text-xs text-slate-600">Certified | SAM.gov</p>
                </div>
              </div>
            </div>

            {/* Use of Funds */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-500" />
                Use of Funds &mdash; $3,415,000
              </h2>
              <div className="space-y-2">
                {[
                  { label: "Building Acquisition", pct: 61, color: "bg-blue-500", amt: "$2.1M" },
                  { label: "Working Capital (12 mo)", pct: 28, color: "bg-emerald-500", amt: "$950K" },
                  { label: "Renovations/Buildout", pct: 4, color: "bg-violet-500", amt: "$150K" },
                  { label: "Equipment & Fixtures", pct: 3, color: "bg-amber-500", amt: "$115K" },
                  { label: "Initial Inventory", pct: 3, color: "bg-cyan-500", amt: "$100K" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-full bg-slate-200 rounded-full h-3">
                      <div className={`${item.color} h-3 rounded-full`} style={{width: `${item.pct}%`}} />
                    </div>
                    <span className="text-xs text-slate-600 whitespace-nowrap w-44">{item.pct}% {item.label} ({item.amt})</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 bg-slate-50 rounded-lg p-3 grid grid-cols-2 gap-2 text-center">
                <div>
                  <p className="text-sm font-bold text-slate-900">SBA Loan: ~$3.07M</p>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Equity: ~$340K</p>
                </div>
              </div>
            </div>

            {/* 3-Year Projections */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-orange-500" />
                3-Year Financial Projections
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-300">
                    <th className="text-left py-2 text-slate-600">Year</th>
                    <th className="text-right py-2 text-slate-600">Revenue</th>
                    <th className="text-right py-2 text-slate-600">Net Income</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200">
                    <td className="py-2 font-medium">2026 (Y1)</td>
                    <td className="py-2 text-right">$2.5M &ndash; $3.2M</td>
                    <td className="py-2 text-right text-slate-600">Break-even to +$200K</td>
                  </tr>
                  <tr className="border-b border-slate-200">
                    <td className="py-2 font-medium">2027 (Y2)</td>
                    <td className="py-2 text-right">$3.5M &ndash; $4.5M</td>
                    <td className="py-2 text-right text-emerald-600 font-semibold">$400K &ndash; $700K</td>
                  </tr>
                  <tr>
                    <td className="py-2 font-medium">2028 (Y3)</td>
                    <td className="py-2 text-right">$5.5M &ndash; $7.0M</td>
                    <td className="py-2 text-right text-emerald-600 font-semibold">$1.2M &ndash; $2.0M</td>
                  </tr>
                </tbody>
              </table>
              <div className="mt-3 bg-emerald-50 rounded-lg p-3 text-center border border-emerald-200">
                <p className="text-sm text-slate-600">Year 2 DSCR</p>
                <p className="text-xl font-bold text-emerald-700">1.3x &mdash; 1.6x</p>
                <p className="text-xs text-slate-500">SBA minimum: 1.15&ndash;1.25x</p>
              </div>
            </div>

            {/* Market Size */}
            <div>
              <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-500" />
                Market Opportunity
              </h2>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-2xl font-bold text-slate-900">$50B+</p>
                  <p className="text-xs text-slate-600">Healthcare Facilities TAM</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">$20B</p>
                  <p className="text-xs text-slate-600">DME Market (US)</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">168K</p>
                  <p className="text-xs text-slate-600">Cars/Day on US-59</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-slate-200 pt-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-slate-900">George Broughton III</p>
              <p className="text-sm text-slate-600">President &amp; Strategic Director</p>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                20121 Highway 59 N, Humble, TX
              </span>
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                george.broughton@sterilemate.com
              </span>
              <span className="flex items-center gap-1">
                <Globe className="w-4 h-4" />
                sterilemate.com
              </span>
            </div>
          </div>
        </div>

        {/* Call to Action - Hide on print */}
        <div className="mt-8 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white print:hidden">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-1">Review Full SBA Business Plan</h3>
              <p className="text-slate-400">Explore the complete investor portal with financial projections and expansion details</p>
            </div>
            <div className="flex gap-3">
              <Link to="/investor">
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Full Investor Portal
                </Button>
              </Link>
              <a href="mailto:george.broughton@sterilemate.com">
                <Button className="bg-cyan-600 hover:bg-cyan-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact George
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
